import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import NotificationModal from '../components/NotificationModal';
import { apiRequest } from '../utils/api';
import { getActiveCompany, getActiveCompanyId } from '../utils/companyContext';
import '../assets/styles/notifications.css';
import { getPreferredEmployeeId } from '../utils/employeeIdentity';

const formatStatus = (status) => {
  if (!status) {
    return 'Inactive';
  }

  return status === 'active' ? 'Active' : 'Inactive';
};

const formatRecipient = (notification) => {
  if (!notification) {
    return '—';
  }

  if (notification.employeeId === 0) {
    return 'All Employees';
  }

  if (notification.adminUserId === 0) {
    return 'All Admin Users';
  }

  if (notification.employee) {
    const parts = [getPreferredEmployeeId(notification.employee) || 'Employee'];
    if (notification.employee.name) {
      parts.push(`- ${notification.employee.name}`);
    }
    return parts.join(' ');
  }

  if (notification.adminUser) {
    const parts = [`Admin #${notification.adminUser.id}`];
    if (notification.adminUser.username) {
      parts.push(`(${notification.adminUser.username})`);
    }
    if (notification.adminUser.name) {
      parts.push(`- ${notification.adminUser.name}`);
    }
    return parts.join(' ');
  }

  if (notification.employeeId) {
    return `Employee #${notification.employeeId}`;
  }

  if (notification.adminUserId) {
    return `Admin #${notification.adminUserId}`;
  }

  return '—';
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());
  const [activeCompany, setActiveCompany] = useState(() => getActiveCompany());

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!activeCompanyId) {
        setNotifications([]);
        setError('Please select a company to view notifications.');
        return;
      }

      const response = await apiRequest(
        `/api/admin/notifications?companyId=${activeCompanyId}`,
        {},
        navigate,
      );
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, navigate]);

  useEffect(() => {
    const handleCompanyChange = (event) => {
      const company = event.detail ?? null;
      setActiveCompanyId(company?.id ?? null);
      setActiveCompany(company);
    };

    window.addEventListener('activeCompanyChanged', handleCompanyChange);
    return () => window.removeEventListener('activeCompanyChanged', handleCompanyChange);
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const notificationCount = useMemo(() => notifications.length, [notifications]);

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort(
        (first, second) =>
          new Date(second.createdAt || 0).getTime() - new Date(first.createdAt || 0).getTime(),
      ),
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return sortedNotifications.filter((notification) => {
      const hasEmployeeAudience = notification.employeeId !== undefined && notification.employeeId !== null;
      const hasAdminAudience = notification.adminUserId !== undefined && notification.adminUserId !== null;

      if (recipientFilter === 'employee' && !hasEmployeeAudience) {
        return false;
      }

      if (recipientFilter === 'admin' && !hasAdminAudience) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const messageText = (notification.message || '').toLowerCase();
      const recipientText = formatRecipient(notification).toLowerCase();
      const statusText = formatStatus(notification.status).toLowerCase();

      return (
        messageText.includes(normalizedSearch) ||
        recipientText.includes(normalizedSearch) ||
        statusText.includes(normalizedSearch)
      );
    });
  }, [recipientFilter, searchQuery, sortedNotifications]);

  const filteredCount = useMemo(() => filteredNotifications.length, [filteredNotifications]);

  const getAudienceLabel = useCallback((notification) => {
    const hasEmployeeAudience = notification.employeeId !== undefined && notification.employeeId !== null;
    const hasAdminAudience = notification.adminUserId !== undefined && notification.adminUserId !== null;

    if (hasEmployeeAudience && hasAdminAudience) {
      return 'Employees & Admin Users';
    }

    if (hasEmployeeAudience) {
      return 'Employees';
    }

    if (hasAdminAudience) {
      return 'Admin Users';
    }

    return 'General';
  }, []);

  const handleCloseModal = () => {
    if (submitting) {
      return;
    }

    setShowModal(false);
    setSubmitError(null);
  };

  const handleCreateNotification = async (payload) => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      if (!activeCompanyId) {
        setSubmitError('Please select a company before creating notifications.');
        return;
      }
      await apiRequest(
        '/api/admin/notifications',
        {
          method: 'POST',
          body: payload,
        },
        navigate,
      );
      setShowModal(false);
      await loadNotifications();
    } catch (err) {
      setSubmitError(err.message || 'Failed to create notification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Notifications">
      <div className="notifications-page container-fluid py-4">
        <div className="notifications-header d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-4">
          <div>
            <h5 className="mb-1">Notification List</h5>
            <p className="text-muted mb-0">
              {notificationCount === 0
                ? 'Create a notification to inform your team.'
                : `Showing ${filteredCount} of ${notificationCount} notification${notificationCount === 1 ? '' : 's'}.`}
            </p>
            {activeCompany && (
              <div className="text-muted small">
                Active company: {activeCompany.name || 'Selected company'}
              </div>
            )}
          </div>
          <button
            type="button"
            className="notifications-pill-button d-flex align-items-center justify-content-center"
            onClick={() => setShowModal(true)}
            disabled={!activeCompanyId}
          >
            Add Notification
          </button>
        </div>

        {!activeCompanyId && (
          <div className="alert alert-warning" role="alert">
            Select a company from the header to view and create notifications.
          </div>
        )}

        <div className="notifications-filters shadow-sm mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-lg-6">
              <label className="form-label text-muted small fw-semibold text-uppercase">Search</label>
              <div className="notifications-search-control">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  className="form-control notifications-input"
                  placeholder="Search by message, recipient, or status"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-3">
              <label className="form-label text-muted small fw-semibold text-uppercase">Recipient Type</label>
              <select
                className="form-select notifications-select"
                value={recipientFilter}
                onChange={(event) => setRecipientFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="employee">Employees</option>
                <option value="admin">Admin Users</option>
              </select>
            </div>
            <div className="col-lg-3">
              <label className="form-label text-muted small fw-semibold text-uppercase">Status</label>
              <div className="notifications-status-summary">
                <span className="badge bg-light text-muted">Active: {notifications.filter((item) => item.status === 'active').length}</span>
                <span className="badge bg-light text-muted">Inactive: {notifications.filter((item) => item.status !== 'active').length}</span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        <div className="card notifications-table-card shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="py-5 text-center">
                <div className="spinner-border text-primary" role="status" aria-hidden="true" />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="py-5 text-center text-muted">
                {notificationCount === 0
                  ? 'No notifications available yet.'
                  : 'No notifications match your search.'}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0 notifications-table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '28%' }}>Message</th>
                      <th style={{ width: '22%' }}>Recipient</th>
                      <th style={{ width: '15%' }}>Audience</th>
                      <th style={{ width: '10%' }}>Status</th>
                      <th style={{ width: '10%' }}>Created On</th>
                      <th style={{ width: '10%' }}>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNotifications.map((notification, index) => (
                      <tr key={notification.id ?? index}>
                        <td className="fw-semibold">{index + 1}</td>
                        <td className="text-break">{notification.message || '—'}</td>
                        <td className="text-break">{formatRecipient(notification)}</td>
                        <td>
                          <span className="notifications-audience-badge">
                            {getAudienceLabel(notification)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              notification.status === 'active'
                                ? 'bg-success-subtle text-success'
                                : 'bg-secondary-subtle text-secondary'
                            }`}
                          >
                            {formatStatus(notification.status)}
                          </span>
                        </td>
                        <td>{formatDateTime(notification.createdAt)}</td>
                        <td>{formatDateTime(notification.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <NotificationModal
        show={showModal}
        onClose={handleCloseModal}
        onSubmit={handleCreateNotification}
        submitting={submitting}
        error={submitError}
      />
    </DashboardLayout>
  );
};

export default Notifications;
